import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  InsertProjectSharingAccessInviteBody,
  ProjectSharingAccess,
  ProjectSharingAccessInviteProjectData,
  UpdateProjectSharingAccessBody,
} from "./types/projectSharingAccesses";

export const useProjectSharingAccessesApi = () => {
  const api = useApi();

  const getBaseUrl = useCallback((projectId: string, isAdmin?: boolean) => {
    return `${isAdmin ? "/admin" : ""}/projects/${projectId}/sharing_accesses`;
  }, []);

  const mutateProjectSharingAccesses = useCallback((projectId: string) => {
    mutateCache([
      `/projects`,
      `/admin/projects`,
      `/projects/${projectId}/sharing_accesses`,
      `/admin/projects/${projectId}/sharing_accesses`,
    ]);
  }, []);

  const useSearchProjectSharingAccesses = useCallback(
    (projectId: string, query: PaginationQuery, signal: AbortSignal, isAdmin?: boolean) => {
      const url = buildUrl(getBaseUrl(projectId, isAdmin), query);
      return useSWR<Paginated<ProjectSharingAccess>, CustomError, string>(url, (url) =>
        api.get<Paginated<ProjectSharingAccess>>({ url, signal }),
      );
    },
    [api, getBaseUrl],
  );

  const useProjectSharingAccessInviteProjectData = useCallback(
    (token: string) => {
      const encodedToken = encodeURIComponent(token);
      const url = `/projects/sharing_accesses/invites/${encodedToken}/project_data`;
      return useSWRImmutable<ProjectSharingAccessInviteProjectData, CustomError, string>(url, (url) =>
        api.get<ProjectSharingAccessInviteProjectData>({ url }),
      );
    },
    [api],
  );

  const insertProjectSharingAccessInvite = useCallback(
    async (projectId: string, body: InsertProjectSharingAccessInviteBody, isAdmin?: boolean): Promise<void> => {
      await api.post<ProjectSharingAccess, InsertProjectSharingAccessInviteBody>(
        `${getBaseUrl(projectId, isAdmin)}/invites`,
        body,
      );
      mutateProjectSharingAccesses(projectId);
    },
    [api, getBaseUrl, mutateProjectSharingAccesses],
  );

  const resendProjectSharingAccessInvite = useCallback(
    async (projectId: string, accessId: string, isAdmin?: boolean): Promise<void> => {
      await api.postNoResult(`${getBaseUrl(projectId, isAdmin)}/invites/${accessId}/re_send`);
      mutateProjectSharingAccesses(projectId);
    },
    [api, getBaseUrl, mutateProjectSharingAccesses],
  );

  const cancelProjectSharingAccessInvite = useCallback(
    async (projectId: string, accessId: string): Promise<void> => {
      await api.put(`${getBaseUrl(projectId)}/invites/${accessId}/cancel`, {});
      mutateProjectSharingAccesses(projectId);
    },
    [api, getBaseUrl, mutateProjectSharingAccesses],
  );

  const acceptProjectSharingAccessInvite = useCallback(
    async (token: string): Promise<{ projectId: string }> => {
      const result = await api.post<{ projectId: string }, { token: string }>(
        `/projects/sharing_accesses/invites/accept`,
        { token },
      );
      mutateProjectSharingAccesses(result.projectId);
      return result;
    },
    [api, mutateProjectSharingAccesses],
  );

  const rejectProjectSharingAccessInvite = useCallback(
    async (token: string): Promise<void> => {
      await api.postNoResult(`/projects/sharing_accesses/invites/reject`, { token });
      mutateCache([`/projects`, `/admin/projects`]);
    },
    [api],
  );

  const updateProjectSharingAccess = useCallback(
    async (
      projectId: string,
      accessId: string,
      body: UpdateProjectSharingAccessBody,
      isAdmin?: boolean,
    ): Promise<void> => {
      await api.put<UpdateProjectSharingAccessBody>(`${getBaseUrl(projectId, isAdmin)}/${accessId}`, body);
      mutateProjectSharingAccesses(projectId);
    },
    [api, getBaseUrl, mutateProjectSharingAccesses],
  );

  const deleteProjectSharingAccess = useCallback(
    async (projectId: string, accessId: string, isAdmin?: boolean): Promise<void> => {
      await api.del(`${getBaseUrl(projectId, isAdmin)}/${accessId}`);
      mutateProjectSharingAccesses(projectId);
    },
    [api, getBaseUrl, mutateProjectSharingAccesses],
  );

  return {
    useSearchProjectSharingAccesses,
    useProjectSharingAccessInviteProjectData,
    insertProjectSharingAccessInvite,
    resendProjectSharingAccessInvite,
    cancelProjectSharingAccessInvite,
    acceptProjectSharingAccessInvite,
    rejectProjectSharingAccessInvite,
    updateProjectSharingAccess,
    deleteProjectSharingAccess,
  };
};
